import {
  DIAGNOSTIC_TRIGGERS,
  DIAGNOSTIC_TRIGGER_LABEL,
  fetchDiagnosticReport,
  fetchDiagnosticReports,
  type DiagnosticReportRow,
  type DiagnosticTrigger,
} from '@auctisync/api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  readDetail,
  readError,
  readEvents,
  readHitTests,
  readRecentTouches,
  readRecovery,
  readStuckGestures,
  readTouchesOff,
  readWindows,
} from './appReport';

/**
 * Super admin — what the Cataloging app reported from the field.
 *
 * Jim, 2026-10-05: "build a diagnostics layer under the cataloging app and leave
 * it there. It could be helpful for future problems when people are actually
 * using the app" — then "Route the reports to the Super Tenant Dash." Reports
 * arrive three ways: the app's touch watchdog saw the screen stop taking
 * touches, the app's code threw, or a worker pressed "Report a problem". Each
 * carries the screen as iOS saw it and what the worker did before.
 *
 * Kept 90 days, then purged. Super admins only: the server raises for anyone
 * else, so the wrong account sees "not available", never an empty list that
 * looks like "no problems".
 */

const WINDOWS = [7, 30, 90] as const;

export function SuperAppReports() {
  const [days, setDays] = useState<number>(30);
  const [trigger, setTrigger] = useState<DiagnosticTrigger | ''>('');
  const [tenantId, setTenantId] = useState<string>('');
  const [rows, setRows] = useState<DiagnosticReportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    const { rows: found, error: err } = await fetchDiagnosticReports(supabase, {
      days,
      trigger: trigger || null,
      tenantId: tenantId || null,
    });
    if (err) {
      setError(
        /42501|administrator/i.test(err.message)
          ? 'App reports are available to platform administrators.'
          : err.message,
      );
      return;
    }
    setRows(found);
  }, [days, trigger, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The houses in view, for the house filter — named as the reports name them.
  const houses = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows ?? [])
      if (r.tenant_id) seen.set(r.tenant_id, r.tenant_name ?? r.tenant_id);
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  return (
    <div style={wrapStyle}>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1 style={h1Style}>App reports</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          What the Cataloging app sent from the field: the screen stopping taking touches, app
          errors, and workers pressing “Report a problem”. Each shows what a touch would have landed
          on, anything stuck or covering the screen, and what the worker did before. Kept 90 days.
        </p>
      </header>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        {WINDOWS.map((w) => (
          <button key={w} type="button" onClick={() => setDays(w)} style={pillStyle(w === days)}>
            Last {w} days
          </button>
        ))}
        <select
          aria-label="Reason"
          value={trigger}
          onChange={(e) => setTrigger(e.target.value as DiagnosticTrigger | '')}
          style={selectStyle}
        >
          <option value="">Every reason</option>
          {DIAGNOSTIC_TRIGGERS.map((t) => (
            <option key={t} value={t}>
              {DIAGNOSTIC_TRIGGER_LABEL[t]}
            </option>
          ))}
        </select>
        <select
          aria-label="House"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          style={selectStyle}
        >
          <option value="">Every house</option>
          {houses.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      ) : rows === null ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div style={cardStyle}>
          <p style={{ color: 'var(--text)', margin: 0 }}>No reports in this window.</p>
          <p style={{ color: 'var(--text-muted)', margin: '0.4rem 0 0', fontSize: '0.85rem' }}>
            The Cataloging app sends one when its screen stops taking touches, when its code fails,
            or when a worker taps “Report a problem”.
          </p>
        </div>
      ) : (
        <div style={cardStyle}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={thStyle}>When</th>
                <th style={thStyle}>Reason</th>
                <th style={thStyle}>House · sender</th>
                <th style={thStyle}>Device</th>
                <th style={thStyle}>Recovered</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <ReportRow
                  key={r.id}
                  row={r}
                  open={openId === r.id}
                  onToggle={() => setOpenId(openId === r.id ? null : r.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReportRow({
  row,
  open,
  onToggle,
}: {
  row: DiagnosticReportRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        style={{
          borderTop: '1px solid var(--border)',
          cursor: 'pointer',
          background: open ? 'var(--surface)' : undefined,
        }}
        aria-expanded={open}
      >
        <td style={tdStyle}>
          {new Date(row.received_at).toLocaleString()}
          {row.note ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.2rem' }}>
              “{row.note.length > 90 ? `${row.note.slice(0, 90)}…` : row.note}”
            </div>
          ) : null}
        </td>
        <td style={{ ...tdStyle, fontWeight: 600 }}>
          {DIAGNOSTIC_TRIGGER_LABEL[row.trigger] ?? row.trigger}
        </td>
        <td style={tdStyle}>
          {row.tenant_name ?? 'No house'}
          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            {row.user_email ?? '—'}
          </div>
        </td>
        <td style={tdStyle}>
          {row.device_model ?? '—'}
          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            {row.platform ?? ''} {row.os_version ?? ''} · app {row.app_version ?? '?'} (
            {row.app_build ?? '?'})
          </div>
        </td>
        <td style={tdStyle}>
          {row.recovered === true ? 'Yes' : row.recovered === false ? 'No' : '—'}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={5} style={{ padding: '0.5rem 0.4rem 1.25rem' }}>
            <ReportDetail row={row} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ReportDetail({ row }: { row: DiagnosticReportRow }) {
  const [report, setReport] = useState<Record<string, unknown> | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchDiagnosticReport(supabase, row.id).then(({ report: r, error: err }) => {
      if (cancelled) return;
      if (err) setError(err.message);
      else setReport(r);
    });
    return () => {
      cancelled = true;
    };
  }, [row.id]);

  if (error) return <div style={errorStyle}>{error}</div>;
  if (report === undefined) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  if (report === null) return <p style={{ color: 'var(--text-muted)' }}>This report is gone.</p>;

  const detail = readDetail(report);
  const recovery = readRecovery(report);
  const hits = readHitTests(report);
  const stuck = readStuckGestures(report);
  const windows = readWindows(report);
  const touches = readRecentTouches(report);
  const events = readEvents(report);
  const failure = readError(report);

  const download = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `app-report-${row.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <div style={detailCardStyle}>
        {row.note ? <p style={{ margin: '0 0 0.5rem' }}>The worker wrote: “{row.note}”</p> : null}
        {detail ? <p style={{ margin: '0 0 0.5rem' }}>{detail}</p> : null}
        {recovery.outcome ? (
          <p style={{ margin: '0 0 0.5rem' }}>
            The app reset {recovery.reset.length ? recovery.reset.join(', ') : 'nothing'} —{' '}
            <strong>{recovery.outcome}</strong>.
          </p>
        ) : null}
        {readTouchesOff(report) ? (
          <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>The app had touches switched off.</p>
        ) : null}
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          Lot {row.lot_id ?? '—'} · sale {row.auction_id ?? '—'} · made{' '}
          {new Date(row.reported_at).toLocaleString()} · {Math.round(row.payload_bytes / 1024)} KB{' '}
          <button type="button" onClick={download} style={linkButtonStyle}>
            Download the full report
          </button>
        </p>
      </div>

      {failure ? (
        <Section title="The app error">
          <pre style={preStyle}>
            {failure.message}
            {'\n'}
            {failure.stack}
          </pre>
        </Section>
      ) : null}

      <Section title="What a touch would have landed on">
        {hits.length === 0 ? (
          <Empty>Not recorded.</Empty>
        ) : (
          <table style={tableStyle}>
            <tbody>
              {hits.map((h) => (
                <tr key={h.point} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                    {h.point}
                  </td>
                  <td style={tdStyle}>
                    <strong>{h.top}</strong>
                    {h.blocked ? (
                      <div style={{ color: 'var(--semantic-error, #b91c1c)', fontSize: '0.8rem' }}>
                        touches off at {h.blocked}
                      </div>
                    ) : null}
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                      in {h.above.join(' › ') || '—'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Gestures not idle">
        {stuck.length === 0 ? (
          <Empty>None — no gesture was stuck mid-gesture.</Empty>
        ) : (
          <ul style={listStyle}>
            {stuck.map((g, i) => (
              <li key={`${g.name}-${String(i)}`}>
                <strong>{g.name}</strong> {g.state}, holding {g.touches} touch
                {g.touches === 1 ? '' : 'es'}, on {g.on}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Windows">
        <ul style={listStyle}>
          {windows.map((w, i) => (
            <li key={`${w.name}-${String(i)}`}>
              <strong>{w.name}</strong> level {w.level}
              {w.flags.length ? ` · ${w.flags.join(', ')}` : ''}
              {w.presented.length ? ` · showing ${w.presented.join(' › ')}` : ''}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Last touches">
        {touches.length === 0 ? (
          <Empty>None recorded.</Empty>
        ) : (
          <table style={tableStyle}>
            <tbody>
              {touches.map((t, i) => (
                <tr key={`${t.when}-${String(i)}`} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {t.when}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{t.point}</td>
                  <td style={tdStyle}>
                    {t.on}
                    {t.inApp ? '' : ' (outside the app)'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="What happened before">
        {events.length === 0 ? (
          <Empty>No events recorded.</Empty>
        ) : (
          <table style={tableStyle}>
            <tbody>
              {events.map((e, i) => (
                <tr key={`${e.when}-${String(i)}`} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {e.when}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{e.type}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{e.data}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={detailCardStyle}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>{children}</p>;
}

const wrapStyle: React.CSSProperties = {
  padding: '2rem 2.25rem',
  maxWidth: '72rem',
};

const h1Style: React.CSSProperties = {
  fontFamily: 'var(--font-heading-active)',
  fontSize: '1.75rem',
  fontWeight: 700,
  color: 'var(--text)',
  margin: 0,
};

const h2Style: React.CSSProperties = {
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  margin: '0 0 0.5rem',
};

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
  overflowX: 'auto',
};

const detailCardStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-elev)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
};

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.4rem',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.4rem',
  color: 'var(--text)',
  verticalAlign: 'top',
};

const listStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: '1.1rem',
  lineHeight: 1.6,
};

const preStyle: React.CSSProperties = {
  margin: 0,
  whiteSpace: 'pre-wrap',
  fontSize: '0.8rem',
  maxHeight: '18rem',
  overflow: 'auto',
};

const selectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.85rem',
};

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--brand-primary)',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 'inherit',
};

function pillStyle(on: boolean): React.CSSProperties {
  return {
    padding: '0.4rem 0.8rem',
    borderRadius: 'var(--r-tile, 0.375rem)',
    border: '1px solid var(--admin-hairline, var(--border))',
    background: on ? 'var(--brand-primary)' : 'var(--surface)',
    color: on ? 'var(--brand-primary-fg, #fff)' : 'var(--text)',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  };
}
